import { Fragment } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { observer } from 'mobx-react-lite';
import styled from 'styled-components';

import { Avatar } from '@components/avatar';
import { IconBrandHire } from '@components/icons/brand-hire';
import { IconCareer } from '@components/icons/career';
import { IconChevronRightSmall } from '@components/icons/chevron-right-small';
import { IconCompany } from '@components/icons/company';
import { IconHelp } from '@components/icons/help';
import { IconJobboard } from '@components/icons/jobboard';
import { IconNotifications } from '@components/icons/notifications';
import { IconOpenNewTab } from '@components/icons/open-new-tab';
import { IconPerson } from '@components/icons/person';
import { IconSettings } from '@components/icons/settings';
import { IconShare } from '@components/icons/share';
import { IconSignout } from '@components/icons/signout';
import { IconStatus } from '@components/icons/status';
import { IconSupport } from '@components/icons/support';
import { Menu } from '@components/menu';
import { Nav } from '@components/nav';
import { HireNav } from '@components/nav/nav';
import { useIntercom } from '@hooks/use-intercom';
import { theme } from '@styles/theme';
import { getVisibleWidth } from '@utils/browser';
import { pxToRem } from '@utils/converters';

import { config } from 'app-config';

import { useNotificationCounts } from 'api/notifications/hooks';
import { useAuthenticatedUser, useSignOut, useSwitchCompany } from 'features/authentication';
import { usePermissions } from 'features/permissions';
import { useNavigationItems } from 'hooks/use-navigation-items';
import { useStore } from 'models/Provider';

import logotypeSvg from './50logo-Hire-logotype.svg';
import { Favorites } from './favorites';

const statusPageUrl = 'https://50skills.statuspage.io/';
const helpCenterUrl = 'https://help.50skills.com/';

export const MainNavigation = observer(() => {
  const { t } = useStore();
  const items = useNavigationItems();
  const { company } = useAuthenticatedUser();
  const { hasRolePermission } = usePermissions();

  const notificationsCountQuery = useNotificationCounts();
  const unread = notificationsCountQuery.data?.unread ?? 0;
  const { showMessenger, isEnabled: isIntercomEnabled } = useIntercom();

  const settingsHref = hasRolePermission('company:view-settings')
    ? '/settings/company'
    : '/settings/profile/details';

  return (
    <HireNav
      stateStorageKey="main-nav-state"
      initialFixed={getVisibleWidth() > 1600}
      fixedHeader={
        <Nav.Header
          logo={({ isOpen }) =>
            isOpen ? <img src={logotypeSvg} alt="50skills Hire" /> : <IconBrandHire />
          }
        >
          <UserMenu />
          <CompanySelector />
        </Nav.Header>
      }
    >
      <Nav.Group>
        {items.map((item) => (
          <Nav.Link key={item.path} href={item.path} label={item.label} iconLeft={item.icon} />
        ))}
      </Nav.Group>
      <Nav.Separator />
      <Nav.Group>
        {hasRolePermission('notifications:view') && (
          <NotificationsContainer>
            <Nav.Link
              iconLeft={<IconNotifications size={16} />}
              label={t('user.tabs.notifications')}
              href="/notifications"
            />
            {unread > 0 && (
              <UnreadNotifications>{unread > 99 ? '99+' : unread}</UnreadNotifications>
            )}
          </NotificationsContainer>
        )}
        <Nav.Link
          href={settingsHref}
          iconLeft={<IconSettings size={16} />}
          label={t('usermenu.company.settings')}
        />
        <Nav.Link
          href="/sharing"
          label={t('navigation.sharing')}
          iconLeft={<IconShare size={16} />}
        />
        <Nav.ExternalLink
          href={`${config.CAREERS_URL}/${company.name}`}
          label={t('navigation.careersite')}
          iconLeft={<IconCareer size={16} />}
          iconRight={<IconOpenNewTab size={16} />}
        />
        {isIntercomEnabled && (
          <Nav.Button
            onClick={showMessenger}
            label={t('navigation.supportchat')}
            iconLeft={<IconSupport size={16} />}
          />
        )}
      </Nav.Group>
      <Nav.Group>
        <Nav.Separator />
        <Favorites />
      </Nav.Group>
    </HireNav>
  );
});

const CompanySelector = () => {
  const { t } = useStore();
  const { companies, company } = useAuthenticatedUser();
  const switchCompany = useSwitchCompany();

  return (
    <Nav.Menu
      icon={<IconCompany size={16} />}
      iconRight={<IconChevronRightSmall />}
      label={company.displayName}
    >
      <Menu.Label label={t('navigation.companies')} icon={<IconCompany />} />
      <Menu.Separator />
      {companies.map((c) => (
        <Nav.MenuItem
          isActive={c.id === company.id}
          key={c.id}
          onSelect={c.id !== company.id ? () => switchCompany.mutate(c) : () => null}
          label={c.displayName}
        />
      ))}
    </Nav.Menu>
  );
};

const UserMenu = () => {
  const { t } = useStore();
  const user = useAuthenticatedUser();
  const { hasRolePermission } = usePermissions();
  const signOut = useSignOut();
  const navigate = useNavigate();
  const location = useLocation();
  const { showMessenger, isEnabled: isIntercomEnabled } = useIntercom();

  return (
    <Nav.Menu
      icon={<Avatar color="purple" name={user.name} src={user.picture} />}
      iconRight={<IconChevronRightSmall />}
      label={user.name}
    >
      <Nav.MenuItem
        onSelect={() =>
          navigate('/settings/profile/details', { state: { from: location.pathname } })
        }
        label={t('usermenu.profile')}
        icon={<IconPerson />}
      />

      {hasRolePermission('notifications:view') && (
        <Nav.MenuItem
          onSelect={() =>
            navigate('/profile/notifications', { state: { from: location.pathname } })
          }
          icon={<IconSettings />}
          label={t('usermenu.notifications')}
        />
      )}
      <Menu.Separator />

      {!isIntercomEnabled && (
        <Nav.MenuItem
          onSelect={showMessenger}
          icon={<IconSupport />}
          label={t('navigation.supportchat')}
        />
      )}
      <Nav.MenuItemLink
        icon={<IconHelp />}
        label={t('usermenu.help')}
        to={helpCenterUrl}
        isExternal
      />
      <Nav.MenuItemLink
        icon={<IconStatus />}
        label={t('usermenu.statusPage')}
        to={statusPageUrl}
        isExternal
      />
      {hasRolePermission('company:view-settings') && (
        <Fragment>
          <Nav.MenuItem
            onSelect={() => navigate('/settings/jobboards', { state: { from: location.pathname } })}
            icon={<IconJobboard />}
            label={t('usermenu.jobboards')}
          />
        </Fragment>
      )}
      <Menu.Separator />
      <Nav.MenuItem
        onSelect={() => signOut.mutate()}
        icon={<IconSignout />}
        label={t('usermenu.logout')}
      />
    </Nav.Menu>
  );
};

const NotificationsContainer = styled.div({
  position: 'relative',
  width: '100%',
});

const UnreadNotifications = styled.div.attrs({
  className: 'unread-notifications',
})({
  position: 'absolute',
  zIndex: 10,
  backgroundColor: theme.color.blue100,
  color: theme.color.white100,
  font: theme.font.semibold10,
  borderRadius: pxToRem(2),
  padding: `${pxToRem(2)} ${pxToRem(4)} ${pxToRem(2)} ${pxToRem(4)}`,
  top: '50%',
  transform: 'translateY(-50%)',
  right: `${pxToRem(8)} !important`,
  left: 'auto !important',
  width: pxToRem(16),
  height: pxToRem(16),
  minWidth: pxToRem(16),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  pointerEvents: 'none',
});
